import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useToolbarPosition } from './hooks/useToolbarPosition';
import { useBulkActions } from './hooks/useBulkActions';
import { normalizeTaskTags } from '../../hooks/useTagActions';
import StatusDropdown from './components/StatusDropdown';
import PriorityDropdown from './components/PriorityDropdown';
import IconPickerDropdown from './components/IconPickerDropdown';
import TagsDropdown from './components/TagsDropdown';
import DueDateDropdown from './components/DueDateDropdown';
import EstimatedDurationDropdown from './components/EstimatedDurationDropdown';
import StartDateDropdown from './components/StartDateDropdown';
import CustomFieldsDropdown from './components/CustomFieldsDropdown';
import { ArchiveIcon, UnarchiveIcon, CloseIcon } from './icons';
import CompileIntoNestedModal from './components/CompileIntoNestedModal';
import { canCompileMissionsIntoNestedCampaign } from '../../shared/nestDependencyInfo';
import './styles/toolbar.css';
import './styles/shared.css';

/**
 * BulkActionsToolbar Component
 * 
 * A floating toolbar that appears when multiple missions are selected.
 * Allows bulk changes to: status, priority, icon, primary tag, archive, dates.
 */
const BulkActionsToolbar = ({
  selectedCount = 0,
  selectedTaskIds = [],
  tasks = [],
  statuses = [],
  allTags = [],
  customFields = [],
  onStatusChange,
  onPriorityChange,
  onIconChange,
  onSetPrimaryTag,
  onAddTag,
  onRemoveTag,
  onArchiveTasks,
  onUnarchiveTask,
  onDueDateChange,
  onEstimatedDurationMinutesChange,
  onStartDateChange,
  onCustomFieldValueChange,
  onClearSelection,
  /** Flow view: show "compile into nested campaign" bulk action */
  flowBulkCompileNestedCampaign = false,
  isMultiCampaignMode = false,
  onCompileMissionsIntoNestedCampaign,
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [compileModalOpen, setCompileModalOpen] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const toolbarRef = useRef(null);
  const iconPickerBtnRef = useRef(null);

  // Position hook for toolbar
  const bottomPosition = useToolbarPosition();

  // Bulk action handlers
  const {
    handleStatusChange,
    handlePriorityChange,
    handleBulkIconChange,
    handlePrimaryTagChange,
    handleBulkAddTag,
    handleBulkRemoveTag,
    handleBulkClearTags,
    handleArchive,
    handleUnarchive,
    handleDueDateChange,
    handleEstimatedDurationMinutesChange,
    handleStartDateChange,
    handleBulkCustomFieldChange,
  } = useBulkActions({
    selectedTaskIds,
    onStatusChange,
    onPriorityChange,
    onIconChange,
    onSetPrimaryTag,
    onAddTag,
    onRemoveTag,
    onArchiveTasks,
    onUnarchiveTask,
    onDueDateChange,
    onEstimatedDurationMinutesChange,
    onStartDateChange,
    onCustomFieldValueChange,
    onClearSelection,
    setActiveDropdown,
    setShowIconPicker,
  });

  // Calculate dropdown position based on button
  const calculatePosition = useCallback((buttonElement) => {
    if (!buttonElement) return { top: 0, left: 0 };
    
    const rect = buttonElement.getBoundingClientRect();
    const menuWidth = 160;
    const padding = 12;
    
    // Center horizontally on button
    let left = rect.left + (rect.width / 2) - (menuWidth / 2);
    
    // Position just above the button (8px gap)
    // We use 'bottom' positioning via a negative offset from button top
    let top = rect.top - 8;
    
    // Keep within horizontal bounds
    if (left < padding) {
      left = padding;
    } else if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - menuWidth - padding;
    }
    
    return { top, left };
  }, []);

  // Toggle dropdown and calculate position
  const toggleDropdown = useCallback((name, buttonElement) => {
    if (activeDropdown === name) {
      setActiveDropdown(null);
    } else {
      const pos = calculatePosition(buttonElement);
      setDropdownPosition(pos);
      setActiveDropdown(name);
    }
  }, [activeDropdown, calculatePosition]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!activeDropdown && !showIconPicker) return;

    const handleClickOutside = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setShowIconPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown, showIconPicker]);

  // Check selected tasks state
  const selectedTasks = tasks.filter(t => selectedTaskIds.includes(t.id));
  const hasArchivedTasks = selectedTasks.some(t => t.archived);
  const hasNonArchivedTasks = selectedTasks.some(t => !t.archived);

  const compileNestedAllowed =
    flowBulkCompileNestedCampaign &&
    !isMultiCampaignMode &&
    canCompileMissionsIntoNestedCampaign(tasks, selectedTaskIds);
  const availableTags = allTags.filter(tag => tag && (tag.id || tag.name));

  // Analyze tags across all selected tasks for the batch tag UI
  const tagAnalysis = useMemo(() => {
    const tagCounts = new Map();
    const presentTags = new Set();
    let tasksWithTags = 0;

    selectedTasks.forEach(task => {
      const taskTags = normalizeTaskTags(task);
      if (taskTags.length > 0) {
        tasksWithTags++;
      }
      taskTags.forEach(tagName => {
        presentTags.add(tagName);
        tagCounts.set(tagName, (tagCounts.get(tagName) || 0) + 1);
      });
    });

    const addableTags = availableTags.filter(tag => {
      const count = tagCounts.get(tag.name) || 0;
      return count < selectedTasks.length;
    });

    const removableTags = availableTags
      .filter(tag => presentTags.has(tag.name))
      .map(tag => ({
        ...tag,
        count: tagCounts.get(tag.name) || 0,
        isOnAll: (tagCounts.get(tag.name) || 0) === selectedTasks.length,
      }));

    return {
      tagCounts,
      presentTags,
      tasksWithTags,
      addableTags,
      removableTags,
      totalSelected: selectedTasks.length,
    };
  }, [selectedTasks, availableTags]);

  // Don't render if no selection
  if (selectedCount < 2) return null;

  const stopPropagation = (e) => e.stopPropagation();

  // Dropdown menu style - position so bottom of menu is at dropdownPosition.top
  const menuStyle = {
    position: 'fixed',
    bottom: `${window.innerHeight - dropdownPosition.top}px`,
    left: `${dropdownPosition.left}px`,
    zIndex: 10001,
    maxHeight: `${dropdownPosition.top - 12}px`,
  };

  return (
    <div 
      className="bulk-actions-toolbar" 
      ref={toolbarRef}
      style={{ bottom: bottomPosition }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {/* Selection count */}
      <div className="bulk-actions-toolbar__count">
        <span className="bulk-actions-toolbar__count-number">{selectedCount}</span>
        <span className="bulk-actions-toolbar__count-label">selected</span>
      </div>

      <div className="bulk-actions-toolbar__divider" />

      {flowBulkCompileNestedCampaign && !isMultiCampaignMode && (
        <>
          <button
            type="button"
            className="bulk-actions-toolbar__btn bulk-actions-toolbar__btn--compile"
            disabled={!compileNestedAllowed}
            title={
              compileNestedAllowed
                ? 'Create a new mission and move the selection into its nested campaign'
                : 'Requires an isolated group, one connected trail with no gaps, or parallel chains sharing one upstream and one downstream node'
            }
            onClick={() => {
              if (!compileNestedAllowed) return;
              setCompileModalOpen(true);
            }}
          >
            <span>📦</span>
            <span>Compile nested</span>
          </button>
          <div className="bulk-actions-toolbar__divider" />
        </>
      )}

      {/* Status dropdown */}
      <StatusDropdown
        isActive={activeDropdown === 'status'}
        onToggle={toggleDropdown}
        onStatusChange={handleStatusChange}
        statuses={statuses}
        menuStyle={menuStyle}
      />

      {/* Priority dropdown */}
      <PriorityDropdown
        isActive={activeDropdown === 'priority'}
        onToggle={toggleDropdown}
        onPriorityChange={handlePriorityChange}
        menuStyle={menuStyle}
      />

      {/* Icon picker */}
      <IconPickerDropdown
        isActive={showIconPicker}
        onToggle={() => {
          setShowIconPicker(!showIconPicker);
          setActiveDropdown(null);
        }}
        onIconChange={handleBulkIconChange}
        onClose={() => setShowIconPicker(false)}
        buttonRef={iconPickerBtnRef}
      />

      {/* Tags dropdown */}
      <TagsDropdown
        isActive={activeDropdown === 'tags'}
        onToggle={toggleDropdown}
        onAddTag={handleBulkAddTag}
        onRemoveTag={handleBulkRemoveTag}
        onClearTags={handleBulkClearTags}
        tagAnalysis={tagAnalysis}
        availableTags={availableTags}
        tasks={tasks}
        menuStyle={menuStyle}
      />

      {/* Due date dropdown */}
      <DueDateDropdown
        isActive={activeDropdown === 'dueDate'}
        onToggle={toggleDropdown}
        onDueDateChange={handleDueDateChange}
        menuStyle={menuStyle}
      />

      {/* Estimated duration dropdown */}
      <EstimatedDurationDropdown
        isActive={activeDropdown === 'estimate'}
        onToggle={toggleDropdown}
        onEstimatedDurationMinutesChange={handleEstimatedDurationMinutesChange}
        menuStyle={menuStyle}
      />

      {/* Start date dropdown */}
      <StartDateDropdown
        isActive={activeDropdown === 'startDate'}
        onToggle={toggleDropdown}
        onStartDateChange={handleStartDateChange}
        menuStyle={menuStyle}
      />

      {/* Custom Fields dropdown */}
      {customFields.length > 0 && (
        <CustomFieldsDropdown
          isActive={activeDropdown === 'customFields'}
          onToggle={toggleDropdown}
          onCustomFieldChange={handleBulkCustomFieldChange}
          customFields={customFields}
          menuStyle={menuStyle}
        />
      )}

      <div className="bulk-actions-toolbar__divider" />

      {/* Unarchive button */}
      {hasArchivedTasks && (
        <button
          className="bulk-actions-toolbar__btn bulk-actions-toolbar__btn--success"
          onClick={handleUnarchive}
          title="Unarchive selected"
        >
          <UnarchiveIcon />
          <span>Restore</span>
        </button>
      )}

      {/* Archive button */}
      {hasNonArchivedTasks && (
        <button
          className="bulk-actions-toolbar__btn bulk-actions-toolbar__btn--danger"
          onClick={handleArchive}
          title="Archive selected"
        >
          <ArchiveIcon />
          <span>Archive</span>
        </button>
      )}

      {/* Clear selection button */}
      <button
        className="bulk-actions-toolbar__btn bulk-actions-toolbar__btn--close"
        onClick={onClearSelection}
        title="Clear selection (Esc)"
      >
        <CloseIcon />
      </button>

      {flowBulkCompileNestedCampaign && !isMultiCampaignMode && (
        <CompileIntoNestedModal
          isOpen={compileModalOpen}
          selectedCount={selectedCount}
          onCancel={() => setCompileModalOpen(false)}
          onConfirm={(missionName) => {
            setCompileModalOpen(false);
            onCompileMissionsIntoNestedCampaign?.({
              missionName,
              draggedIds: selectedTaskIds,
            });
            onClearSelection?.();
          }}
        />
      )}
    </div>
  );
};

export default BulkActionsToolbar;